import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { CategoryTranslationsDialog } from '../components/Translations';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  Category,
  CriticalStock,
  ExpiringLot,
  LowStockSettings,
  NotificationSettings,
  ProductPage,
  VariantStock,
  VariantThreshold,
  Warehouse,
} from '../lib/types';

// ---------------------------------------------------------------------------
// Kategoriler
// ---------------------------------------------------------------------------

export function CategoriesPage() {
  const t = useT();
  const { can } = usePermissions();
  const canWrite = can('catalog.product.write');
  const list = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/v1/admin/categories'),
  });
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [editing, setEditing] = useState<Category | null>(null);
  const [translating, setTranslating] = useState<Category | null>(null);
  const create = useApiMutation(
    () => api.post('/v1/admin/categories', { name, parentId: parentId || null }),
    {
      invalidate: [['categories']],
      success: t('common.saved'),
      onSuccess: () => setName(''),
      silentFieldErrors: true,
    },
  );
  const toggle = useApiMutation(
    (c: Category) =>
      api.patch(
        `/v1/admin/categories/${c.id}`,
        { status: c.status === 'active' ? 'hidden' : 'active' },
        { ifMatch: c.version },
      ),
    { invalidate: [['categories']], success: t('common.saved') },
  );
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/categories/${id}`), {
    invalidate: [['categories']],
    success: t('common.saved'),
  });
  const rows = list.data ?? [];
  const byParent = (pid: string | null) => rows.filter((c) => (c.parentId ?? null) === pid);
  const render = (pid: string | null, depth: number): ReactNode =>
    byParent(pid).map((c) => (
      <li key={c.id}>
        <div
          className="flex flex-wrap items-center gap-3 px-4 py-2.5"
          style={{ paddingInlineStart: `${1 + depth * 1.5}rem` }}
        >
          <span className="flex-1">
            <span className="font-medium">{c.name}</span>{' '}
            <span className="text-sm text-ink-3">/{c.slug}</span>
          </span>
          <span className="text-sm text-ink-3">
            {t('admin.categories.productCount', { count: c.productCount })}
          </span>
          <Badge tone={c.status === 'active' ? 'moss' : 'slate'}>
            {t(`admin.categories.statuses.${c.status}`)}
          </Badge>
          {canWrite && (
            <>
              <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>
                {t('common.edit')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setTranslating(c)}
                aria-label={t('admin.catalogTranslations.categoryTitle', { name: c.name })}
              >
                {t('admin.catalogTranslations.title')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => toggle.mutate(c)}>
                {c.status === 'active' ? t('admin.categories.hide') : t('admin.categories.show')}
              </Button>
              <ConfirmButton
                message={t('admin.categories.confirmDelete', { name: c.name })}
                onConfirm={() => remove.mutateAsync(c.id)}
              >
                {t('common.delete')}
              </ConfirmButton>
            </>
          )}
        </div>
        <ul>{render(c.id, depth + 1)}</ul>
      </li>
    ));
  return (
    <PageFrame helpKey="page.categories">
      {canWrite && (
        <div className="flex flex-wrap items-end gap-2">
          <Field
            label={t('admin.categories.name')}
            help={<HelpTip k="categories.name" />}
            error={fieldError(create.error, 'name')}
          >
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label={t('admin.categories.parent')} help={<HelpTip k="categories.parent" />}>
            {(p) => (
              <Select {...p} value={parentId} onChange={(e) => setParentId(e.target.value)}>
                <option value="">{t('admin.categories.topLevel')}</option>
                {rows.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={name.trim().length < 2}
            onClick={() => create.mutate(undefined)}
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.categories.add')}
          </Button>
        </div>
      )}
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('admin.categories.empty')} description={t('admin.categories.emptyLead')} />
      ) : (
        <>
          <ul className="divide-y divide-line rounded-md border border-line">{render(null, 0)}</ul>
          {canWrite && (
            <p className="flex items-center gap-1 text-sm text-ink-3">
              {t('admin.categories.editHint')}
              <HelpTip k="categories.edit" />
            </p>
          )}
        </>
      )}
      <CategoryDialog category={editing} onClose={() => setEditing(null)} />
      {translating && (
        <CategoryTranslationsDialog category={translating} onClose={() => setTranslating(null)} />
      )}
    </PageFrame>
  );
}

/** Kategori düzenleme: ad, kısa ad (değişirse eski adres yönlendirilir), açıklama ve arama sonuçları. */
function CategoryDialog({ category, onClose }: { category: Category | null; onClose: () => void }) {
  const t = useT();
  const [draft, setDraft] = useState({
    name: '',
    slug: '',
    description: '',
    seoTitle: '',
    seoDescription: '',
  });
  useEffect(() => {
    if (category)
      setDraft({
        name: category.name,
        slug: category.slug,
        description: category.description ?? '',
        seoTitle: category.seoTitle ?? '',
        seoDescription: category.seoDescription ?? '',
      });
  }, [category]);
  const set = (patch: Partial<typeof draft>) => setDraft((d) => ({ ...d, ...patch }));
  const save = useApiMutation(
    () =>
      api.patch(
        `/v1/admin/categories/${category!.id}`,
        {
          name: draft.name,
          slug: draft.slug,
          description: draft.description.trim() || null,
          seoTitle: draft.seoTitle.trim() || null,
          seoDescription: draft.seoDescription.trim() || null,
        },
        { ifMatch: category!.version },
      ),
    { invalidate: [['categories']], success: t('common.saved'), silentFieldErrors: true, onSuccess: onClose },
  );
  const fe = (f: string) => fieldError(save.error, f);
  return (
    <Dialog
      open={!!category}
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.categories.editTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={draft.name.trim().length < 2 || save.isPending}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t('admin.categories.name')} help={<HelpTip k="categories.name" />} error={fe('name')}>
          {(p) => (
            <Input {...p} value={draft.name} maxLength={80} onChange={(e) => set({ name: e.target.value })} />
          )}
        </Field>
        <Field
          label={t('admin.categories.slug')}
          help={<HelpTip k="categories.slug" />}
          description={t('admin.categories.slugLead', { path: `/kategori/${draft.slug}` })}
          error={fe('slug')}
        >
          {(p) => (
            <Input
              {...p}
              value={draft.slug}
              maxLength={80}
              spellCheck={false}
              onChange={(e) => set({ slug: e.target.value.toLowerCase() })}
            />
          )}
        </Field>
        <Field
          label={t('admin.categories.description')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="categories.description" />}
          error={fe('description')}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={draft.description}
              maxLength={2000}
              onChange={(e) => set({ description: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.categories.seoTitle')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="categories.seo_title" />}
          description={t('admin.pages.chars', { count: draft.seoTitle.length, max: 70 })}
          error={fe('seoTitle')}
        >
          {(p) => (
            <Input
              {...p}
              value={draft.seoTitle}
              maxLength={70}
              onChange={(e) => set({ seoTitle: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.categories.seoDescription')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="categories.seo_description" />}
          description={t('admin.pages.chars', { count: draft.seoDescription.length, max: 170 })}
          error={fe('seoDescription')}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              value={draft.seoDescription}
              maxLength={170}
              onChange={(e) => set({ seoDescription: e.target.value })}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Stok
// ---------------------------------------------------------------------------

export function StockPage() {
  const t = useT();
  const { can } = usePermissions();
  return (
    <PageFrame helpKey="page.stock">
      <CriticalStockSection canWrite={can('inventory.stock.write')} />
      <WarehousesSection canWrite={can('inventory.stock.write')} />
      <VariantStockSection canWrite={can('inventory.stock.write')} />
      <ExpiringSection />
      <p className="text-sm text-ink-3">{t('admin.stock.ledgerNote')}</p>
    </PageFrame>
  );
}

function useWarehouses() {
  return useQuery({ queryKey: ['warehouses'], queryFn: () => api.get<Warehouse[]>('/v1/admin/warehouses') });
}

function WarehousesSection({ canWrite }: { canWrite: boolean }) {
  const t = useT();
  const list = useWarehouses();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const create = useApiMutation(
    () => api.post('/v1/admin/warehouses', { name, code, isDefault: false, status: 'active' }),
    {
      invalidate: [['warehouses']],
      success: t('common.saved'),
      onSuccess: () => {
        setName('');
        setCode('');
      },
      silentFieldErrors: true,
    },
  );
  return (
    <Section title={t('admin.stock.warehouses')} description={t('admin.stock.warehousesLead')}>
      <ul className="mb-3 divide-y divide-line rounded-md border border-line">
        {list.data?.map((w) => (
          <li key={w.id} className="flex items-center justify-between px-4 py-2.5">
            <span>
              <span className="font-medium">{w.name}</span>{' '}
              <span className="text-sm text-ink-3">{w.code}</span>
            </span>
            <span className="flex gap-2">
              {w.isDefault && <Badge tone="pine">{t('admin.stock.default')}</Badge>}
              <Badge tone={w.status === 'active' ? 'moss' : 'slate'}>
                {t(`admin.pricing.listStatus.${w.status}`)}
              </Badge>
            </span>
          </li>
        ))}
        {!list.data?.length && (
          <li className="px-4 py-2.5 text-sm text-ink-3">{t('admin.stock.noWarehouses')}</li>
        )}
      </ul>
      {canWrite && (
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t('admin.stock.warehouseName')} error={fieldError(create.error, 'name')}>
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field
            label={t('admin.stock.warehouseCode')}
            error={
              fieldError(create.error, 'code') ??
              (create.error?.code === 'code_taken' ? create.error.title : undefined)
            }
          >
            {(p) => (
              <Input
                {...p}
                className="w-28"
                value={code}
                placeholder="ANA"
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
            )}
          </Field>
          <Button
            loading={create.isPending}
            disabled={!name || !code}
            onClick={() => create.mutate(undefined)}
          >
            {t('admin.stock.addWarehouse')}
          </Button>
        </div>
      )}
    </Section>
  );
}

function VariantStockSection({ canWrite }: { canWrite: boolean }) {
  const t = useT();
  const { formatDate } = useI18n();
  const [q, setQ] = useState('');
  const [variantId, setVariantId] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [open, setOpen] = useState<null | 'change' | 'transfer'>(null);
  const search = useQuery({
    queryKey: ['stock-search', q],
    queryFn: () => api.get<ProductPage>(`/v1/admin/products?q=${encodeURIComponent(q)}&limit=10`),
    enabled: q.trim().length >= 2,
  });
  const stock = useQuery({
    queryKey: ['variant-stock', variantId],
    queryFn: () => api.get<VariantStock>(`/v1/admin/stock/variants/${variantId}`),
    enabled: Boolean(variantId),
  });
  return (
    <Section title={t('admin.stock.variantStock')} description={t('admin.stock.variantStockLead')}>
      <Field label={t('admin.stock.findProduct')}>
        {(p) => (
          <Input
            {...p}
            type="search"
            value={q}
            placeholder={t('admin.products.searchPlaceholder')}
            onChange={(e) => setQ(e.target.value)}
          />
        )}
      </Field>
      {search.data && q.trim().length >= 2 && (
        <ProductVariantPicker
          productIds={search.data.items.map((i) => ({ id: i.id, title: i.title }))}
          onPick={(id, l) => {
            setVariantId(id);
            setLabel(l);
            setQ('');
          }}
        />
      )}
      {variantId && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-medium">{label}</p>
            {canWrite && (
              <div className="flex gap-2">
                <Button size="sm" variant="primary" onClick={() => setOpen('change')}>
                  {t('admin.stock.newMovement')}
                </Button>
                <Button size="sm" onClick={() => setOpen('transfer')}>
                  {t('admin.stock.transfer')}
                </Button>
              </div>
            )}
          </div>
          {stock.isLoading ? (
            <Spinner label={t('common.loading')} />
          ) : (
            <>
              {stock.data && stock.data.levels.every((l) => l.onHand === 0) && (
                <p className="rounded-sm border border-line bg-surface-2 px-3 py-2 text-sm text-ink-2">
                  {t('admin.stock.noStockYet')}
                </p>
              )}
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm">
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.stock.warehouse')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.stock.lot')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.stock.expiry')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('admin.stock.onHand')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('admin.stock.reserved')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stock.data?.levels.map((l, i) => (
                      <tr key={i} className="border-t border-line">
                        <td className="px-3 py-2">{l.warehouseName}</td>
                        <td className="px-3 py-2">{l.lotNo ?? '—'}</td>
                        <td className="px-3 py-2">{l.expiryDate ?? '—'}</td>
                        <td className="px-3 py-2 text-end tabular">{l.onHand}</td>
                        <td className="px-3 py-2 text-end tabular">{l.reserved}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <details>
                <summary className="cursor-pointer text-sm font-medium">{t('admin.stock.movements')}</summary>
                <ul className="mt-2 divide-y divide-line text-sm">
                  {stock.data?.movements.map((m) => (
                    <li key={m.id} className="flex flex-wrap gap-3 py-1.5">
                      <span className="w-40 text-ink-3">{formatDate(m.createdAt, true)}</span>
                      <span className="w-28">{t(`admin.stock.types.${m.type}`)}</span>
                      <span className={m.quantity < 0 ? 'tabular text-rose' : 'tabular text-moss'}>
                        {m.quantity > 0 ? `+${m.quantity}` : m.quantity}
                      </span>
                      <span className="flex-1 text-ink-3">{m.reason ?? ''}</span>
                    </li>
                  ))}
                </ul>
              </details>
              <ThresholdsEditor key={variantId} variantId={variantId} canWrite={canWrite} />
            </>
          )}
        </div>
      )}
      {open === 'change' && variantId && (
        <StockChangeDialog
          variantId={variantId}
          levels={stock.data?.levels ?? []}
          onClose={() => setOpen(null)}
        />
      )}
      {open === 'transfer' && variantId && (
        <TransferDialog
          variantId={variantId}
          levels={stock.data?.levels ?? []}
          onClose={() => setOpen(null)}
          onReceipt={() => setOpen('change')}
        />
      )}
    </Section>
  );
}

/** Ürün arama sonucundan varyant seçimi (ürün ayrıntısı istenerek varyantlar listelenir). */
export function ProductVariantPicker({
  productIds,
  onPick,
}: {
  productIds: Array<{ id: string; title: string }>;
  onPick: (variantId: string, label: string) => void;
}) {
  const t = useT();
  const [productId, setProductId] = useState<string | null>(null);
  const detail = useQuery({
    queryKey: ['product', productId],
    queryFn: () =>
      api.get<{ variants: Array<{ id: string; sku: string; title: string; status: string }>; title: string }>(
        `/v1/admin/products/${productId}`,
      ),
    enabled: Boolean(productId),
  });
  if (!productIds.length) return <p className="mt-2 text-sm text-ink-3">{t('admin.stock.noResults')}</p>;
  return (
    <ul className="mt-2 divide-y divide-line rounded-md border border-line">
      {productIds.map((p) => (
        <li key={p.id} className="px-3 py-2">
          <button
            type="button"
            className="font-medium text-pine hover:underline"
            onClick={() => setProductId(p.id)}
          >
            {p.title}
          </button>
          {productId === p.id && detail.data && (
            <div className="mt-2 flex flex-wrap gap-2">
              {detail.data.variants
                .filter((v) => v.status === 'active')
                .map((v) => (
                  <Button
                    key={v.id}
                    size="sm"
                    onClick={() =>
                      onPick(v.id, `${detail.data!.title}${v.title ? ` — ${v.title}` : ''} (${v.sku})`)
                    }
                  >
                    {v.title || v.sku}
                  </Button>
                ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function StockChangeDialog({
  variantId,
  levels,
  onClose,
}: {
  variantId: string;
  levels: VariantStock['levels'];
  onClose: () => void;
}) {
  const t = useT();
  const warehouses = useWarehouses();
  const [idem] = useState(() => new IdempotencyKey());
  const [f, setF] = useState({
    warehouseId: '',
    type: 'receipt',
    quantity: '',
    reason: '',
    lotNo: '',
    expiryDate: '',
    /** Sayım/düzeltme/hasarda: lot kimliği, 'none' (lotsuz bakiye) veya seçilmedi (''). */
    lot: '',
  });
  const warehouseId =
    f.warehouseId || warehouses.data?.find((w) => w.isDefault)?.id || warehouses.data?.[0]?.id;
  // F10-04: depoda lot varsa sayım/düzeltme/hasar lot seçilerek yapılır (lotsuz bakiyeye yazılmasın).
  const lots = levels.filter((l) => l.warehouseId === warehouseId && l.lotId);
  const pickLot = f.type !== 'receipt' && lots.length > 0;
  const save = useApiMutation(
    () =>
      api.post(
        '/v1/admin/stock/changes',
        {
          variantId,
          warehouseId,
          type: f.type,
          quantity: Number(f.quantity),
          reason: f.reason || null,
          ...(f.type === 'receipt' && f.lotNo
            ? { lot: { lotNo: f.lotNo, expiryDate: f.expiryDate || null } }
            : {}),
          ...(pickLot && f.lot ? { lotId: f.lot === 'none' ? null : f.lot } : {}),
        },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['variant-stock'], ['products'], ['product']],
      success: t('common.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.stock.newMovement')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!f.quantity}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.stock.movementType')} help={<HelpTip k="stock.change_type" />}>
          {(p) => (
            <Select {...p} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
              {['receipt', 'adjustment', 'damage', 'count'].map((x) => (
                <option key={x} value={x}>
                  {t(`admin.stock.types.${x}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.stock.warehouse')}>
          {(p) => (
            <Select
              {...p}
              value={f.warehouseId}
              onChange={(e) => setF({ ...f, warehouseId: e.target.value })}
            >
              {warehouses.data?.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {pickLot && (
          <Field
            label={t('admin.stock.lotPick')}
            required
            help={<HelpTip k="stock.lot_pick" />}
            error={fieldError(save.error, 'lotId')}
          >
            {(p) => (
              <Select {...p} value={f.lot} onChange={(e) => setF({ ...f, lot: e.target.value })}>
                <option value="">{t('admin.stock.chooseLot')}</option>
                {lots.map((l) => (
                  <option key={l.lotId!} value={l.lotId!}>
                    {t('admin.stock.lotOption', {
                      lot: l.lotNo ?? '—',
                      expiry: l.expiryDate ?? '—',
                      onHand: l.onHand,
                    })}
                  </option>
                ))}
                <option value="none">{t('admin.stock.lotless')}</option>
              </Select>
            )}
          </Field>
        )}
        <Field
          label={f.type === 'count' ? t('admin.stock.countedQuantity') : t('admin.stock.quantity')}
          required
          error={fieldError(save.error, 'quantity')}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="numeric"
              value={f.quantity}
              onChange={(e) => setF({ ...f, quantity: e.target.value })}
            />
          )}
        </Field>
        {f.type === 'receipt' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t('admin.stock.lotNo')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="stock.lot" />}
            >
              {(p) => (
                <Input {...p} value={f.lotNo} onChange={(e) => setF({ ...f, lotNo: e.target.value })} />
              )}
            </Field>
            <Field label={t('admin.stock.expiry')} optionalLabel={t('common.optional')}>
              {(p) => (
                <Input
                  {...p}
                  type="date"
                  value={f.expiryDate}
                  onChange={(e) => setF({ ...f, expiryDate: e.target.value })}
                />
              )}
            </Field>
          </div>
        )}
        <Field
          label={t('admin.stock.reason')}
          required={f.type === 'adjustment'}
          optionalLabel={t('common.optional')}
          error={fieldError(save.error, 'reason')}
        >
          {(p) => <Input {...p} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />}
        </Field>
        {save.error && !save.error.errors.length && (
          <p className="text-sm text-rose" role="alert">
            {save.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/**
 * Depolar arası transfer (F3B-14): kaynak olarak yalnızca taşınabilir stoğu olan depo/lot seçilir; stok yoksa
 * transfer yerine ilk girişin "Mal kabul" ile yapılacağı söylenir. En fazla kullanılabilir miktar taşınır.
 */
function TransferDialog({
  variantId,
  levels,
  onClose,
  onReceipt,
}: {
  variantId: string;
  levels: VariantStock['levels'];
  onClose: () => void;
  onReceipt: () => void;
}) {
  const t = useT();
  const warehouses = useWarehouses();
  const [idem] = useState(() => new IdempotencyKey());
  const sources = levels
    .map((l) => ({ ...l, available: l.onHand - l.reserved, value: `${l.warehouseId}|${l.lotId ?? ''}` }))
    .filter((l) => l.available > 0);
  const [f, setF] = useState({ source: sources[0]?.value ?? '', toWarehouseId: '', quantity: '' });
  const src = sources.find((x) => x.value === f.source);
  const qty = Number(f.quantity);
  const tooMany = !!src && Number.isInteger(qty) && qty > src.available;
  const targets = (warehouses.data ?? []).filter((w) => w.status === 'active' && w.id !== src?.warehouseId);
  const save = useApiMutation(
    () =>
      api.post(
        '/v1/admin/stock/transfers',
        {
          variantId,
          fromWarehouseId: src!.warehouseId,
          toWarehouseId: f.toWarehouseId,
          lotId: src!.lotId,
          quantity: qty,
        },
        { idempotencyKey: idem },
      ),
    { invalidate: [['variant-stock']], success: t('common.saved'), onSuccess: onClose },
  );
  const sourceLabel = (l: (typeof sources)[number]) =>
    [
      l.warehouseName,
      l.lotNo ? t('admin.stock.lotLabel', { lot: l.lotNo }) : t('admin.stock.noLot'),
      l.expiryDate ? t('admin.stock.expiryLabel', { date: l.expiryDate }) : null,
      t('admin.stock.availableCount', { count: l.available }),
    ]
      .filter(Boolean)
      .join(' · ');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.stock.transfer')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!src || !f.toWarehouseId || !Number.isInteger(qty) || qty < 1 || tooMany}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      {sources.length === 0 ? (
        <div className="space-y-3">
          <p
            role="alert"
            className="rounded-sm border border-amber/30 bg-amber-tint px-3 py-2 text-sm text-ink"
          >
            {t('admin.stock.transferNoStock')}
          </p>
          <span className="flex items-center gap-1">
            <Button variant="primary" onClick={onReceipt}>
              {t('admin.stock.goReceipt')}
            </Button>
            <HelpTip k="stock.transfer_receipt" />
          </span>
        </div>
      ) : (
        <div className="space-y-4">
          <Field label={t('admin.stock.fromWarehouseId')} help={<HelpTip k="stock.transfer" />}>
            {(p) => (
              <Select
                {...p}
                value={f.source}
                onChange={(e) => setF({ ...f, source: e.target.value, toWarehouseId: '' })}
              >
                {sources.map((l) => (
                  <option key={l.value} value={l.value}>
                    {sourceLabel(l)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.stock.toWarehouseId')}>
            {(p) => (
              <Select
                {...p}
                value={f.toWarehouseId}
                onChange={(e) => setF({ ...f, toWarehouseId: e.target.value })}
              >
                <option value="">—</option>
                {targets.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {targets.length === 0 && <p className="text-sm text-amber">{t('admin.stock.transferNeedsTwo')}</p>}
          <Field
            label={t('admin.stock.quantity')}
            description={src ? t('admin.stock.transferMax', { count: src.available }) : undefined}
            error={
              tooMany
                ? t('admin.stock.transferTooMany', { count: src!.available })
                : fieldError(save.error, 'quantity')
            }
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={f.quantity}
                onChange={(e) => setF({ ...f, quantity: e.target.value })}
              />
            )}
          </Field>
        </div>
      )}
    </Dialog>
  );
}

function ExpiringSection() {
  const t = useT();
  const [days, setDays] = useState('90');
  const list = useQuery({
    queryKey: ['expiring', days],
    queryFn: () => api.get<ExpiringLot[]>(`/v1/admin/stock/expiring?days=${days}`),
  });
  return (
    <Section
      title={t('admin.stock.expiring')}
      description={t('admin.stock.expiringLead')}
      action={<HelpTip k="stock.expiring" />}
    >
      <Field label={t('admin.stock.withinDays')} className="max-w-48">
        {(p) => (
          <Select {...p} value={days} onChange={(e) => setDays(e.target.value)}>
            {['30', '60', '90', '180', '365'].map((d) => (
              <option key={d} value={d}>
                {t('admin.stock.days', { n: d })}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {list.data?.length ? (
        <ul className="mt-3 divide-y divide-line rounded-md border border-line text-sm">
          {list.data.map((l) => (
            <li key={l.lotId} className="flex flex-wrap gap-3 px-4 py-2">
              <span className="w-28 tabular">{l.expiryDate}</span>
              <span className="flex-1">
                {l.productTitle} <span className="text-ink-3">{l.sku}</span>
              </span>
              <span>{t('admin.stock.lotLabel', { lot: l.lotNo })}</span>
              <span className="tabular">{l.onHand}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-ink-3">{t('admin.stock.noExpiring')}</p>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Kritik stok (F3B-20)
// ---------------------------------------------------------------------------

const SOURCE_TONE = { warehouse: 'amber', variant: 'amber', default: 'slate' } as const;

function CriticalStockSection({ canWrite }: { canWrite: boolean }) {
  const t = useT();
  const { can } = usePermissions();
  const list = useQuery({
    queryKey: ['stock-critical'],
    queryFn: () => api.get<CriticalStock>('/v1/admin/stock/critical'),
  });
  const cfg = useQuery({
    queryKey: ['stock-alert-settings'],
    queryFn: () => api.get<LowStockSettings>('/v1/admin/stock/alert-settings'),
  });
  const notifications = useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: () => api.get<NotificationSettings>('/v1/admin/settings/notifications'),
    enabled: can('org.settings.update'),
  });
  const [draft, setDraft] = useState<string | null>(null);
  const saved = cfg.data?.defaultThreshold;
  const value = draft ?? (saved === null || saved === undefined ? '' : String(saved));
  const save = useApiMutation(
    () =>
      api.put('/v1/admin/stock/alert-settings', {
        defaultThreshold: value.trim() === '' ? null : Number(value),
      }),
    {
      invalidate: [['stock-alert-settings'], ['stock-critical']],
      success: t('admin.stock.critical.defaultSaved'),
      onSuccess: () => setDraft(null),
    },
  );
  const digest = useApiMutation(
    (on: boolean) =>
      api.put('/v1/admin/settings/notifications', { ...notifications.data, lowStockAlerts: on }),
    {
      invalidate: [['settings', 'notifications'], ['stock-alert-settings']],
      success: t('common.saved'),
    },
  );
  const items = list.data?.items ?? [];
  return (
    <Section
      title={t('admin.stock.critical.title')}
      description={t('admin.stock.critical.lead')}
      action={<HelpTip k="stock.critical" />}
    >
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : items.length === 0 ? (
        <p className="text-sm text-ink-2">{t('admin.stock.critical.none')}</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" aria-label={t('admin.stock.critical.title')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.stock.critical.product')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.stock.warehouse')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.stock.critical.available')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.stock.critical.threshold')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.stock.critical.rule')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={`${i.variantId}-${i.warehouseId ?? 'all'}`} className="border-t border-line">
                    <td className="px-3 py-2">
                      {i.productTitle}
                      {i.variantTitle && <span className="text-ink-2"> · {i.variantTitle}</span>}
                      <span className="block text-xs text-ink-3">{i.sku}</span>
                    </td>
                    <td className="px-3 py-2">
                      {i.warehouseName ?? t('admin.stock.critical.allWarehouses')}
                    </td>
                    <td
                      className={
                        i.available === 0
                          ? 'px-3 py-2 text-end tabular text-rose'
                          : 'px-3 py-2 text-end tabular'
                      }
                    >
                      {i.available}
                    </td>
                    <td className="px-3 py-2 text-end tabular">{i.threshold}</td>
                    <td className="px-3 py-2">
                      <Badge tone={SOURCE_TONE[i.source]}>
                        {t(`admin.stock.critical.sources.${i.source}`)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {list.data && list.data.total > items.length && (
            <p className="mt-2 text-sm text-ink-3">
              {t('admin.stock.critical.more', { shown: items.length, total: list.data.total })}
            </p>
          )}
        </>
      )}
      <div className="mt-4 flex flex-wrap items-end gap-x-6 gap-y-3">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(undefined);
          }}
        >
          <Field
            label={t('admin.stock.critical.defaultThreshold')}
            description={t('admin.stock.critical.defaultThresholdHint')}
            help={<HelpTip k="stock.default_threshold" />}
            error={fieldError(save.error, 'defaultThreshold')}
            className="max-w-xs"
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                min={0}
                inputMode="numeric"
                value={value}
                disabled={!canWrite}
                onChange={(e) => setDraft(e.target.value)}
              />
            )}
          </Field>
          {canWrite && (
            <Button type="submit" loading={save.isPending} disabled={draft === null}>
              {t('admin.stock.critical.saveDefault')}
            </Button>
          )}
        </form>
        {can('org.settings.update') && notifications.data && (
          <span className="flex items-center gap-2 pb-2">
            <Switch
              checked={notifications.data.lowStockAlerts}
              onCheckedChange={(v) => digest.mutate(v)}
              label={t('admin.stock.critical.dailyDigest')}
            />
            <span className="text-sm">{t('admin.stock.critical.dailyDigest')}</span>
            <HelpTip k="stock.daily_digest" />
          </span>
        )}
        {!can('org.settings.update') && cfg.data && (
          <p className="pb-2 text-sm text-ink-2">
            {cfg.data.dailyDigest ? t('admin.stock.critical.digestOn') : t('admin.stock.critical.digestOff')}
          </p>
        )}
      </div>
    </Section>
  );
}

/** Varyantın kritik stok eşikleri: tüm depolar toplamı ve depo bazında (boş: eşik yok). */
function ThresholdsEditor({ variantId, canWrite }: { variantId: string; canWrite: boolean }) {
  const t = useT();
  const warehouses = useWarehouses();
  const q = useQuery({
    queryKey: ['stock-thresholds', variantId],
    queryFn: () => api.get<VariantThreshold[]>(`/v1/admin/stock/variants/${variantId}/thresholds`),
  });
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const savedMap = Object.fromEntries(
    (q.data ?? []).map((r) => [r.warehouseId ?? 'all', String(r.minAvailable)]),
  );
  const values = draft ?? savedMap;
  const rows = [
    { key: 'all', label: t('admin.stock.critical.allWarehouses') },
    ...(warehouses.data ?? [])
      .filter((w) => w.status === 'active')
      .map((w) => ({ key: w.id, label: w.name })),
  ];
  const save = useApiMutation(
    () =>
      api.put(`/v1/admin/stock/variants/${variantId}/thresholds`, {
        thresholds: Object.entries(values)
          .filter(([, v]) => v.trim() !== '')
          .map(([k, v]) => ({ warehouseId: k === 'all' ? null : k, minAvailable: Number(v) })),
      }),
    {
      invalidate: [['stock-thresholds', variantId], ['stock-critical']],
      success: t('admin.stock.critical.thresholdsSaved'),
      onSuccess: () => setDraft(null),
    },
  );
  if (q.isLoading) return <Spinner label={t('common.loading')} />;
  return (
    <form
      className="space-y-3 rounded-md border border-line p-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(undefined);
      }}
    >
      <p className="flex items-center gap-1 font-medium">
        {t('admin.stock.critical.thresholds')}
        <HelpTip k="stock.thresholds" />
      </p>
      <p className="text-sm text-ink-2">{t('admin.stock.critical.thresholdsLead')}</p>
      <div className="grid gap-3 sm:grid-cols-3">
        {rows.map((r, i) => (
          <Field key={r.key} label={r.label} error={fieldError(save.error, `thresholds.${i}.minAvailable`)}>
            {(p) => (
              <Input
                {...p}
                type="number"
                min={0}
                inputMode="numeric"
                value={values[r.key] ?? ''}
                disabled={!canWrite}
                onChange={(e) => setDraft({ ...values, [r.key]: e.target.value })}
              />
            )}
          </Field>
        ))}
      </div>
      {canWrite && (
        <span className="flex items-center gap-1">
          <Button type="submit" size="sm" loading={save.isPending} disabled={draft === null}>
            {t('admin.stock.critical.saveThresholds')}
          </Button>
          <HelpTip k="stock.thresholds_save" />
        </span>
      )}
    </form>
  );
}
